/**
 * The caller's local calendar date, ISO `YYYY-MM-DD`.
 *
 * Route-owned code (`frontend/src/views/**`, `frontend/src/components/**`
 * outside the design system) is route-purity scanned and may not import
 * `dayjs` directly (ROUTE_PURITY.md: pages describe, they do not compute).
 * `frontend/src/utils/**` is outside that boundary, so this is the one place
 * a route/hook derives "today" without opening that import itself.
 */
import dayjs from 'dayjs';

export function todayIsoDate(): string {
  return dayjs().format('YYYY-MM-DD');
}

/** Day and month always two digits, so the printed date and the field mask have one fixed width. */
const LOCAL_DATE: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/** An ISO `YYYY-MM-DD` date as the owner's locale prints it: the same text a date field shows. */
export function formatLocalDate(isoDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, LOCAL_DATE).format(new Date(`${isoDate}T00:00:00`));
}

/**
 * The dayjs pattern of `formatLocalDate`, read from the locale itself, so a
 * date field shows and accepts dates exactly as the rest of the page prints
 * them (12.11.2026 in Finnish, never the ISO form beside it).
 */
export function localDatePattern(locale: string): string {
  return new Intl.DateTimeFormat(locale, LOCAL_DATE)
    .formatToParts(new Date(2000, 0, 2))
    .map((part) => {
      if (part.type === 'day') return 'DD';
      if (part.type === 'month') return 'MM';
      if (part.type === 'year') return 'YYYY';
      // The picker's input mask takes separators literally; a date locale's
      // separators are punctuation, never a dayjs token letter.
      return part.value;
    })
    .join('');
}
